<script setup lang="ts">
  import { computed, ref, watch } from 'vue'
  import { Temporal } from '@js-temporal/polyfill'
  import type { ISODateTimeString } from '@gat-ship/types'
  import { IconCalendar } from '@gat-ship/icons'
  import { Input } from '../ui/input'
  import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover'
  import { DateCalendar } from '../date-calendar'
  import { useDateInput } from './composables/useDateInput'
  import { useTimeInput } from './composables/useTimeInput'
  import { useDatePicker } from './composables/useDatePicker'
  import { getCurrentTimeZoneId } from './composables/date-utils'
  import { useUserPreferences } from '../../composables/useUserPreferences'
  import { Button } from '../ui/button'

  /**
   * Standalone date-and-time-input primitive — a date `Input`, a
   * calendar popover, and a time `Input`. Form-agnostic.
   *
   * The date and time halves are tracked **independently** (`datePart` /
   * `timePart`). Each is held as a full instant for the composables'
   * sake, but only `datePart`'s date and `timePart`'s time-of-day are
   * meaningful. The bound value is their combination and stays
   * `undefined` until BOTH halves are filled — the component never
   * invents a time-of-day, so a date entered without a time leaves the
   * field empty (a `required` validator then flags it) rather than
   * silently defaulting to noon.
   *
   * It is a plain controlled component: value in via
   * `modelValue`, edits out via `update:modelValue` / `blur`. It must
   * NOT read the tanstack `field` directly — `FieldApi.state` is not a
   * Vue reactive source, so a `computed` over it would never re-run.
   * `FormDateTimeField` does the reactive read inside the `form.Field`
   * slot and feeds the value down here.
   */

  const props = defineProps<{
    /** The bound instant. An empty string is treated as no value. */
    modelValue: ISODateTimeString | string | undefined
    name: string
    disabled?: boolean
    dataTestid?: string
    hasErrors?: boolean
    /**
     * The field's tanstack `isTouched` flag, forwarded by
     * `FormDateTimeField`. A form reset flips it back to `false`; the
     * control watches that falling edge as its reset signal (see the
     * reset `watch` below) — `modelValue` alone can't reveal a reset
     * because a date-only partial state already reads as empty.
     */
    fieldTouched?: boolean
  }>()

  const emit = defineEmits<{
    'update:modelValue': [value: ISODateTimeString | undefined]
    blur: []
  }>()

  // The user's date-format preference drives the date half's display
  // and parsing; `useDateInput` falls back to the runtime locale.
  const getUserPreferences = useUserPreferences()
  const dateFormat = computed(() => getUserPreferences().dateFormat)

  const timeZone = getCurrentTimeZoneId()

  // The two halves. The date inputs (text + calendar) edit `datePart`;
  // the time input edits `timePart`. For each, only one half of the
  // instant is meaningful — the other is a throwaway the composables
  // need but `syncModel` never reads.
  const datePart = ref<ISODateTimeString | undefined>()
  const timePart = ref<ISODateTimeString | undefined>()

  /** Split the bound value back into the two independently-held halves. */
  function seedFromModel() {
    const value = props.modelValue
      ? (props.modelValue as ISODateTimeString)
      : undefined
    datePart.value = value
    timePart.value = value
  }

  // Re-seed both halves when the bound value changes from the outside
  // (initial value, edit, AI fill). Our own commits are skipped via
  // `lastEmitted` — without that guard, echoing an `undefined` commit
  // back would wipe a date the user entered before the time.
  let lastEmitted: ISODateTimeString | undefined
  watch(
    () => props.modelValue,
    (raw) => {
      const value = raw ? (raw as ISODateTimeString) : undefined
      if (value === lastEmitted) return
      seedFromModel()
    },
    { immediate: true }
  )

  // A form reset is invisible through `modelValue` alone: a date-only
  // partial state already emits an empty value, so resetting to empty
  // doesn't change the prop. The field's `isTouched` flag does flip
  // back to `false` on reset, so that falling edge is the reset signal
  // — re-seed to drop any half-entered date the model never carried.
  watch(
    () => props.fieldTouched,
    (touched, wasTouched) => {
      if (wasTouched && !touched) seedFromModel()
    }
  )

  /**
   * Combine the calendar date of `datePart` with the clock time of
   * `timePart` and emit it — or emit `undefined` while either half is
   * still missing, so the field reads as empty until it is complete.
   */
  function syncModel() {
    let next: ISODateTimeString | undefined
    if (datePart.value && timePart.value) {
      const date = Temporal.Instant.from(datePart.value).toZonedDateTimeISO(
        timeZone
      )
      const time = Temporal.Instant.from(timePart.value).toZonedDateTimeISO(
        timeZone
      )
      next = date
        .with({
          hour: time.hour,
          minute: time.minute,
          second: 0,
          millisecond: 0,
          microsecond: 0,
          nanosecond: 0
        })
        .toInstant()
        .toString() as ISODateTimeString
    }
    lastEmitted = next
    emit('update:modelValue', next)
  }

  // The date half (text input + calendar) edits `datePart`; the time
  // half edits `timePart`. Each commit recombines via `syncModel`.
  const {
    dateInputValue,
    formatPattern,
    onFocus: onDateFocus,
    onInput: onDateInput,
    onBlur: onDateBlur
  } = useDateInput(
    { modelValue: () => datePart.value, locale: dateFormat },
    (value) => {
      datePart.value = value
      syncModel()
    }
  )

  const {
    timeInputValue,
    onFocus: onTimeFocus,
    onInput: onTimeInput,
    onBlur: onTimeBlur
  } = useTimeInput({ modelValue: () => timePart.value }, (value) => {
    timePart.value = value
    syncModel()
  })

  function handleDateBlur() {
    onDateBlur()
    emit('blur')
  }

  function handleTimeBlur() {
    onTimeBlur()
    emit('blur')
  }

  // Calendar popover for picking the date half — writes `datePart`, the
  // same target as the date `Input`.
  // fallow-ignore-next-line code-duplication
  const calendarOpen = ref(false)

  const { selectedDate, handleDateChange } = useDatePicker(
    {
      modelValue: () => datePart.value,
      timeZone: undefined,
      anchorTime: undefined
    },
    (value) => {
      datePart.value = value
      syncModel()
    }
  )

  // Commit the pick, signal blur so the field runs blur-time validation,
  // then close the popover.
  function onCalendarChange(date: Parameters<typeof handleDateChange>[0]) {
    handleDateChange(date)
    emit('blur')
    calendarOpen.value = false
  }
</script>

<template>
  <div class="flex w-full items-center gap-1">
    <Input
      :id="name"
      :model-value="dateInputValue"
      :placeholder="formatPattern"
      :data-testid="dataTestid"
      :disabled="disabled"
      :aria-invalid="hasErrors || undefined"
      class="w-full max-w-[86px] tabular-nums"
      @focus="onDateFocus"
      @update:model-value="(value) => onDateInput(String(value))"
      @blur="handleDateBlur"
    />

    <Input
      :model-value="timeInputValue"
      placeholder="hh:mm"
      :disabled="disabled"
      :aria-invalid="hasErrors || undefined"
      class="w-[100px] tabular-nums"
      @focus="onTimeFocus"
      @update:model-value="(value) => onTimeInput(String(value))"
      @blur="handleTimeBlur"
    />
    <Popover v-model:open="calendarOpen">
      <PopoverTrigger as-child>
        <Button
          :disabled="disabled"
          class="ml-auto size-7 shrink-0 p-0"
          variant="ghost"
          size="icon"
          aria-label="Open calendar"
        >
          <IconCalendar class="size-3.5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent as-child align="start">
        <DateCalendar
          mode="single"
          :model-value="selectedDate"
          @update:model-value="onCalendarChange"
        />
      </PopoverContent>
    </Popover>
  </div>
</template>
